import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Plus, Trash2, Save, CloudUpload, CheckCircle2, XCircle, Clock,
  Beaker, TestTube2, Loader2, ChevronRight, AlertTriangle, Upload, Image,
  Calculator, DollarSign, ClipboardCheck, FileUp, X, Package, Truck, Download, ExternalLink, FileImage, Paperclip
} from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { format } from "date-fns";
import { 
  saveDraftLocally, 
  loadDraftLocally, 
  markDraftSynced, 
  markDraftSyncFailed,
  generateDraftKey,
  removeDraftLocally
} from "@/utils/draftManager";

// Test Status Options (as per requirement)
const TEST_STATUS_OPTIONS = ["Not Started", "Started", "End", "Complete", "Finish"];

// Trial options for extraction column selection (T1-T10)
const TRIAL_EXTRACTION_OPTIONS = ["T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8", "T9", "T10"];

// Test Result Form Options
const FINAL_TEST_NO_OPTIONS = ["T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8", "T9", "T10", "T11", "T12", "T13", "T14", "T15"];
const TEST_STATUS_FORM_OPTIONS = ["Pass", "Fail", "Under Observation"];
const APPROVAL_STATUS_OPTIONS = ["Approved", "Rejected", "Under Observation"];
// Multi-select Observations Options
const OBSERVATIONS_OPTIONS = [
  "Smooth texture",
  "No separation observed",
  "Stable color",
  "Odor stability",
  "Consistent viscosity"
];

// Dropdown options (must match Zoho Creator dropdown values)
const ODOUR_OPTIONS = ["Pleasant", "Fragrance-Free", "Medicinal", "Floral", "Citrus"];
const TEXTURE_OPTIONS = ["Gel", "Cream", "Lotion", "Serum", "Spray", "Liquid"];
const COLOUR_OPTIONS = ["Transparent", "White", "Off-White", "Cream", "Pink", "Light Yellow", "Pale Green", "Golden", "Amber"];
const TEST_RESULT_OPTIONS = ["Pass", "In Progress", "Fail"];

const STATUS_COLORS = {
  "Not Started": "bg-slate-100 text-slate-700",
  "Started": "bg-blue-100 text-blue-700",
  "End": "bg-amber-100 text-amber-700",
  "Complete": "bg-emerald-100 text-emerald-700",
  "Finish": "bg-vcc-teal/20 text-vcc-teal",
  "Pass": "bg-green-100 text-green-700",
  "In Progress": "bg-blue-100 text-blue-700",
  "Fail": "bg-red-100 text-red-700"
};

const SYNC_COLORS = {
  "Synced": "text-emerald-500",
  "Failed": "text-red-500",
  "Pending": "text-amber-500"
};

export default function ProductFolderDetailPage() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeIteration, setActiveIteration] = useState(1);
  const [showNewIterationModal, setShowNewIterationModal] = useState(false);
  const [creatingIteration, setCreatingIteration] = useState(false);
  const [lastSubmittedIteration, setLastSubmittedIteration] = useState(null); // Track submitted iteration for summary
  
  // File extraction states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extractionComplete, setExtractionComplete] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedTrialForExtraction, setSelectedTrialForExtraction] = useState("T1"); // Trial column selection for extraction
  
  // Ingredient validation states
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  
  // Confirmation dialog states
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showExtractConfirm, setShowExtractConfirm] = useState(false);
  const [showDeleteRowConfirm, setShowDeleteRowConfirm] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  
  // Delete iteration state
  const [showDeleteIterationConfirm, setShowDeleteIterationConfirm] = useState(false);
  const [iterationToDelete, setIterationToDelete] = useState(null);
  const [deleteIterationIndex, setDeleteIterationIndex] = useState(-1);
  const [deletingIteration, setDeletingIteration] = useState(false);
  
  // Product Composition Details (Subform)
  const [productDetails, setProductDetails] = useState([]);
  
  // Testing Details
  const [testingDetails, setTestingDetails] = useState({
    methodOfPreparation: "",
    observation: "",
    description: "",
    phRange: "",
    phInitial: "",
    phFinal: "",
    viscosity: "",
    odour: "",
    texture: "",
    color: "",
    testResult: "",
    remarks: "",
    directionOfUse: ""
  });
  
  // Test Status (new dropdown)
  const [testStatus, setTestStatus] = useState("Not Started");
  
  // Stock deduction toggle - controlled by global setting (admin can change)
  const [stockDeductionEnabled, setStockDeductionEnabled] = useState(true);
  const [globalStockDeductionEnabled, setGlobalStockDeductionEnabled] = useState(true);
  const [loadingGlobalSetting, setLoadingGlobalSetting] = useState(true);
  
  // Iteration SKU - for stock deduction (auto-populated from folder, editable per iteration)
  const [iterationSku, setIterationSku] = useState("");

  // Lab Employees for Tester dropdown
  const [labEmployees, setLabEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Test Result Form states
  const [showTestResultForm, setShowTestResultForm] = useState(false);
  const [submittingTestResult, setSubmittingTestResult] = useState(false);
  const [testResultForm, setTestResultForm] = useState({
    submissionDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    sampleNo: "",
    tester: "",
    testerLookupId: "", // Zoho lookup ID for Tester
    finalTestNo: "T1",
    testStatusResult: "Pass", // Default to Pass
    approvalStatus: "Approved",
    observations: [], // Multi-select array
    comments: ""
  });
  const [testResultFile, setTestResultFile] = useState(null);
  const testResultFileRef = useRef(null);

  // Sample Dispatch Form states
  const [showDispatchForm, setShowDispatchForm] = useState(false);
  const [submittingDispatch, setSubmittingDispatch] = useState(false);
  const [testResultSubmitted, setTestResultSubmitted] = useState(false);
  const [dispatchSubmitted, setDispatchSubmitted] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({
    sampleNo: "",
    productName: "",
    clientName: "",
    clientEmail: "",
    contactNumber: "",
    productType: "",
    packingType: "",
    location: "",
    samplePriority: "",
    sku: "",
    quantity: "",
    sampleImage: "",
    trackingNumber: "",
    dispatchDetails: [{ 
      modeOfDispatch: "", 
      dispatchType: "", 
      courierName: "", 
      dispatchDate: "", 
      expectedDeliveryDate: "" 
    }],
    handoverInfo: [{ 
      dispatchedBy: "", 
      handoverTime: "", 
      fileUpload: "" 
    }],
    remarks: "",
    additionalRequirements: "",
    dispatchStatus: "Pending"
  });
  const [dispatchImageFile, setDispatchImageFile] = useState(null);
  const dispatchImageRef = useRef(null);

  const fetchFolder = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/product-folders/${folderId}`);
      if (response.data.success) {
        const data = response.data.folder;
        setFolder(data);
        setTestStatus(data.testStatus || "Not Started");
        
        // Check if test result has been submitted
        setTestResultSubmitted(data.testResultSubmitted || false);
        
        // Set active iteration to latest
        if (data.iterations?.length > 0) {
          const latest = Math.max(...data.iterations.map(it => it.iterationNo));
          setActiveIteration(latest);
          loadIterationData(data.iterations.find(it => it.iterationNo === latest));
        }
        
        // Check if dispatch has been submitted for this sample
        if (data.sampleNo) {
          try {
            const dispatchResp = await axios.get(`${API}/sample-dispatch/${data.sampleNo}`);
            if (dispatchResp.data.success && dispatchResp.data.dispatch) {
              setDispatchSubmitted(true);
            }
          } catch (e) {
            // No dispatch found, that's fine
            setDispatchSubmitted(false);
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch folder:", error);
      toast.error("Failed to load product details");
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  const loadIterationData = (iteration) => {
    if (!iteration) return;
    
    // Check for pending local draft FIRST
    const draftKey = generateDraftKey('iteration', folderId, iteration.iterationNo);
    const localDraft = loadDraftLocally(draftKey);
    
    if (localDraft && localDraft.metadata?.syncStatus === 'pending' && localDraft.data) {
      // There's an unsynced local draft - use it instead of server data
      const savedAt = localDraft.metadata.savedAt 
        ? new Date(localDraft.metadata.savedAt).toLocaleString() 
        : 'unknown time';
      
      toast.info(`📋 Found unsaved local draft (${savedAt})`, {
        description: "Loading your local changes and syncing to server...",
        duration: 4000,
        style: { background: '#DBEAFE', color: '#1E40AF', border: '1px solid #3B82F6' }
      });
      
      // Load local draft data
      if (localDraft.data.productDetails) {
        setProductDetails(localDraft.data.productDetails.map((pd, i) => ({
          id: i + 1,
          items: pd.items || "",
          inci: pd.inci || "",
          vendorDetails: pd.vendorDetails || "",
          composition: pd.composition || "",
          function: pd.function || "",
          fetchingFunction: false
        })));
      } else {
        setProductDetails([]);
      }
      
      if (localDraft.data.testingDetails) {
        setTestingDetails(localDraft.data.testingDetails);
      }
      
      if (localDraft.data.testStatus) {
        setTestStatus(localDraft.data.testStatus);
      }
      
      if (localDraft.data.sku) {
        setIterationSku(localDraft.data.sku);
      } else {
        setIterationSku(iteration.sku || folder?.sku || "");
      }
      
      // Try to sync the local draft to server now that server is available
      syncPendingDraftToServer(draftKey, localDraft.data, iteration.iterationNo);
      
      return; // Don't load server data, use local draft
    }
    
    // No pending local draft - load from server data as normal
    setProductDetails(iteration.productDetails?.map((pd, i) => ({
      id: i + 1,
      items: pd.items || "",
      inci: pd.inci || "",
      vendorDetails: pd.vendorDetails || "",
      composition: pd.composition || "",
      function: pd.function || "",
      fetchingFunction: false
    })) || []);
    
    // Load iteration SKU (fallback to folder SKU if not set)
    setIterationSku(iteration.sku || folder?.sku || "");
    
    // Load testing details
    setTestingDetails(iteration.testingDetails || {
      methodOfPreparation: "",
      observation: "",
      description: "",
      phRange: "",
      phInitial: "",
      phFinal: "",
      viscosity: "",
      odour: "",
      texture: "",
      color: "",
      testResult: "",
      remarks: "",
      directionOfUse: ""
    });
  };
  
  // Function to sync pending local draft to server
  const syncPendingDraftToServer = async (draftKey, draftData, iterationNo) => {
    try {
      const payload = {
        productDetails: draftData.productDetails?.map(pd => ({
          items: pd.items || "",
          inci: pd.inci || "",
          vendorDetails: pd.vendorDetails || "",
          composition: pd.composition || "",
          function: pd.function || ""
        })) || [],
        testingDetails: draftData.testingDetails || {},
        testStatus: draftData.testStatus || "Not Started",
        sku: draftData.sku || ""
      };
      
      const response = await axios.put(
        `${API}/product-folders/${folderId}/iterations/${iterationNo}`,
        payload
      );
      
      if (response.data.success) {
        // Mark draft as synced and remove from local storage
        markDraftSynced(draftKey);
        removeDraftLocally(draftKey);
        toast.success("✅ Local draft synced to server!", {
          duration: 3000,
          style: { background: '#D1FAE5', color: '#065F46', border: '1px solid #10B981' }
        });
        // Refresh to confirm sync
        fetchFolder();
      } else {
        toast.warning("⚠️ Could not sync draft to server. Changes saved locally.", {
          duration: 4000
        });
      }
    } catch (error) {
      console.error("Failed to sync draft to server:", error);
      toast.warning("⚠️ Server sync failed. Your changes are saved locally.", {
        duration: 4000
      });
    }
  };

  useEffect(() => {
    fetchFolder();
  }, [fetchFolder]);

  // Fetch global stock deduction setting
  const fetchGlobalStockDeductionSetting = useCallback(async () => {
    setLoadingGlobalSetting(true);
    try {
      const response = await axios.get(`${API}/settings/stock-deduction`);
      if (response.data.success) {
        const enabled = response.data.enabled;
        setGlobalStockDeductionEnabled(enabled);
        setStockDeductionEnabled(enabled); // Sync local state with global
      }
    } catch (error) {
      console.error("Failed to fetch global stock deduction setting:", error);
    } finally {
      setLoadingGlobalSetting(false);
    }
  }, []);

  useEffect(() => {
    fetchGlobalStockDeductionSetting();
  }, [fetchGlobalStockDeductionSetting]);

  // Toggle global stock deduction (Admin only)
  const handleToggleGlobalStockDeduction = async (enabled) => {
    if (!isAdmin) {
      toast.error("Only Admin can change global stock deduction settings");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/settings/stock-deduction`, null, {
        params: { 
          enabled, 
          userId: user?.id, 
          userName: user?.name, 
          userRole: user?.role 
        }
      });
      
      if (response.data.success) {
        setGlobalStockDeductionEnabled(enabled);
        setStockDeductionEnabled(enabled);
        toast.success(response.data.message);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to update setting";
      toast.error(errorMsg);
    }
  };

  // Fetch Lab Employees for Tester dropdown
  const fetchLabEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      const response = await axios.get(`${API}/zoho/lab-employees`);
      if (response.data.success) {
        setLabEmployees(response.data.employees || []);
      }
    } catch (error) {
      console.error("Failed to fetch lab employees:", error);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    fetchLabEmployees();
  }, [fetchLabEmployees]);

  const handleIterationChange = (iterationNo) => {
    setActiveIteration(iterationNo);
    const iteration = folder.iterations?.find(it => it.iterationNo === iterationNo);
    loadIterationData(iteration);
  };

  // Product Composition handlers
  const handleAddProductRow = () => {
    const newId = productDetails.length > 0 ? Math.max(...productDetails.map(r => r.id)) + 1 : 1;
    setProductDetails([...productDetails, { 
      id: newId, 
      items: "", 
      inci: "", 
      vendorDetails: "", 
      composition: "", 
      function: "",
      fetchingFunction: false
    }]);
  };

  const handleRemoveProductRow = (id) => {
    setRowToDelete(id);
    setShowDeleteRowConfirm(true);
  };

  const confirmDeleteRow = () => {
    if (rowToDelete !== null) {
      setProductDetails(productDetails.filter(row => row.id !== rowToDelete));
      toast.success("Ingredient row removed");
    }
    setRowToDelete(null);
    setShowDeleteRowConfirm(false);
  };

  // Auto-fetch function when ingredient name changes
  const fetchIngredientFunction = async (id, ingredientName) => {
    if (!ingredientName || ingredientName.length < 2) return;
    
    // Set loading state for this row
    setProductDetails(prev => prev.map(row => 
      row.id === id ? { ...row, fetchingFunction: true } : row
    ));
    
    try {
      const response = await axios.get(`${API}/ingredient-function`, {
        params: { ingredient: ingredientName }
      });
      
      if (response.data.success && response.data.function) {
        setProductDetails(prev => prev.map(row => 
          row.id === id ? { ...row, function: response.data.function, fetchingFunction: false } : row
        ));
      } else {
        setProductDetails(prev => prev.map(row => 
          row.id === id ? { ...row, fetchingFunction: false } : row
        ));
      }
    } catch (error) {
      console.error("Failed to fetch ingredient function:", error);
      setProductDetails(prev => prev.map(row => 
        row.id === id ? { ...row, fetchingFunction: false } : row
      ));
    }
  };

  const handleProductChange = (id, field, value) => {
    setProductDetails(productDetails.map(row => {
      if (row.id === id) {
        return { ...row, [field]: value };
      }
      return row;
    }));
    
    // Auto-fetch function when ingredient name changes (with debounce)
    if (field === "items" && value.length >= 3) {
      // Debounce the API call
      const timeoutId = setTimeout(() => {
        fetchIngredientFunction(id, value);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  };

  const handleTestingChange = (field, value) => {
    setTestingDetails(prev => ({ ...prev, [field]: value }));
  };

  // File upload handlers for AI extraction
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setUploadedFiles(prev => [...prev, ...files]);
    setExtractionComplete(false);
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setExtractionComplete(false);
  };

  // Validate ingredients against Zoho Ingredient_Master before submission
  const validateIngredients = async () => {
    setValidating(true);
    setValidationError(null);
    try {
      const payload = {
        productDetails: productDetails.map(pd => ({
          items: pd.items || "",
          inci: pd.inci || "",
          vendorDetails: pd.vendorDetails || "",
          composition: pd.composition || "",
          function: pd.function || ""
        }))
      };

      const response = await axios.post(`${API}/validate-ingredients`, payload);
      
      if (!response.data.success) {
        const errorObj = {
          error: response.data.error,
          missingIngredients: response.data.missingIngredients || [],
          noStockIngredients: response.data.noStockIngredients || []
        };
        setValidationError(errorObj);
        toast.error(errorObj.error); // Show toast for immediate feedback
        return false;
      }
      return true;
    } catch (error) {
      console.error("Validation error:", error);
      const errorObj = {
        error: "Failed to validate ingredients. Please try again.",
        missingIngredients: [],
        noStockIngredients: []
      };
      setValidationError(errorObj);
      toast.error(errorObj.error);
      return false;
    } finally {
      setValidating(false);
    }
  };

  const handleExtractFromImages = async () => {
    if (uploadedFiles.length < 1) {
      toast.error("Please upload at least 1 image (formulation or testing sheet)");
      return;
    }

    // First validate ingredients if any exist
    if (productDetails.length > 0) {
      toast.info("Validating existing ingredients before extraction...");
      const isValid = await validateIngredients();
      if (!isValid) {
        toast.error(validationError?.error || "Ingredient validation failed. Fix issues before extraction.");
        return;
      }
    }

    setExtracting(true);
    try {
      const formData = new FormData();
      uploadedFiles.forEach(file => formData.append("files", file));
      formData.append("trialNo", selectedTrialForExtraction); // Use selected trial column (T1-T10)

      const response = await axios.post(`${API}/ai/extract`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success && response.data.data) {
        const extractedData = response.data.data;
        
        // Append extracted product details to existing rows
        if (extractedData.productDetails?.length > 0) {
          const newRows = extractedData.productDetails.map((pd, i) => ({
            id: productDetails.length + i + 1,
            items: pd.items || "",
            inci: pd.inci || "",
            vendorDetails: pd.vendorDetails || "",
            composition: pd.composition || "",
            function: pd.function || "",
            fetchingFunction: false
          }));
          
          setProductDetails(prev => [...prev, ...newRows]);
        }
        
        // Update testing details if extracted
        if (extractedData.testingDetails) {
          setTestingDetails(prev => ({
            ...prev,
            ...extractedData.testingDetails
          }));
        }
        
        setExtractionComplete(true);
        toast.success(`Extracted ${extractedData.productDetails?.length || 0} ingredients from ${selectedTrialForExtraction} column!`);
      } else {
        toast.error(response.data.error || "Extraction failed");
      }
    } catch (error) {
      console.error("Extraction error:", error);
      toast.error(error.response?.data?.detail || "Failed to extract data from images");
    } finally {
      setExtracting(false);
    }
  };

  const clearUploadedFiles = () => {
    setUploadedFiles([]);
    setExtractionComplete(false);
  };

  // Save iteration data
  const handleSave = async () => {
    setSaving(true);
    
    const payload = {
      productDetails: productDetails.map(pd => ({
        items: pd.items || "",
        inci: pd.inci || "",
        vendorDetails: pd.vendorDetails || "",
        composition: pd.composition || "",
        function: pd.function || ""
      })),
      testingDetails,
      testStatus,
      sku: iterationSku  // Save iteration-specific SKU
    };
    
    // Generate unique draft key for this iteration
    const draftKey = generateDraftKey('iteration', folderId, activeIteration);
    
    // Step 1: Save to localStorage IMMEDIATELY (offline-first)
    const localSaveResult = saveDraftLocally(draftKey, payload, {
      folderId,
      iterationNo: activeIteration,
      folderName: folder?.name || '',
      type: 'iteration'
    });
    
    if (localSaveResult.success) {
      toast.success("✅ Draft saved locally!", {
        duration: 2000,
        style: { background: '#D1FAE5', color: '#065F46', border: '1px solid #10B981' }
      });
    }
    
    // Step 2: Try to sync to server in background
    try {
      const response = await axios.put(
        `${API}/product-folders/${folderId}/iterations/${activeIteration}`,
        payload
      );
      
      if (response.data.success) {
        // Mark draft as synced and remove from local storage
        markDraftSynced(draftKey);
        removeDraftLocally(draftKey);
        toast.success("✅ Synced to server!", {
          duration: 3000,
          style: { background: '#D1FAE5', color: '#065F46', border: '1px solid #10B981' }
        });
        fetchFolder();
      } else {
        markDraftSyncFailed(draftKey, response.data.message || "Server sync failed");
        toast.warning("⚠️ Saved locally. Server sync pending.", {
          description: "Your data is safe. Will sync when server is available.",
          duration: 4000,
          style: { background: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' }
        });
      }
    } catch (error) {
      // Server unavailable - draft is already saved locally
      markDraftSyncFailed(draftKey, error.message || "Network error");
      toast.warning("⚠️ Saved locally. Server offline.", {
        description: "Your data is safe in local storage. Will sync automatically when connection is restored.",
        duration: 5000,
        style: { background: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' }
      });
    } finally {
      setSaving(false);
    }
  };

  // Submit to Zoho (auto-saves first with ingredient validation)
  const handleSubmitToZoho = async () => {
    // Check if iteration is already synced to Zoho
    const currentIt = folder?.iterations?.find(it => it.iterationNo === activeIteration);
    if (currentIt?.zohoSyncStatus === "Synced" || currentIt?.zohoRecordId || currentIt?.zohoNpdRecordId) {
      toast.error("This iteration has already been submitted and synced to Zoho.", {
        duration: 5000,
        style: { background: '#FEE2E2', color: '#991B1B', border: '2px solid #DC2626' }
      });
      return; // Cancel submission
    }
    
    // Show confirmation dialog first
    setShowSubmitConfirm(true);
  };

  // Validate vendor stock before submission
  const validateVendorStock = async () => {
    try {
      // Use iteration SKU, fallback to folder SKU
      const skuSize = iterationSku ? parseFloat(iterationSku) : (folder?.sku ? parseFloat(folder.sku) : 100);
      const payload = {
        productDetails: productDetails.map(pd => ({
          items: pd.items || "",
          inci: pd.inci || "",
          vendorDetails: pd.vendorDetails || "",
          composition: pd.composition || "",
          function: pd.function || ""
        })),
        skuSize: skuSize
      };

      const response = await axios.post(`${API}/validate-vendor-stock`, payload);
      
      if (!response.data.success) {
        setValidationError({
          error: "Vendor stock validation failed. Please check the issues below:",
          missingIngredients: [],
          noStockIngredients: [],
          vendorErrors: response.data.errors || []
        });
        return false;
      }
      
      // Show warnings if any
      if (response.data.warnings?.length > 0) {
        response.data.warnings.forEach(w => toast.warning(w));
      }
      
      return true;
    } catch (error) {
      console.error("Vendor validation error:", error);
      toast.error("Failed to validate vendor stock");
      return false;
    }
  };

  // Actual submission after confirmation
  const confirmSubmitToZoho = async () => {
    setShowSubmitConfirm(false);
    setSubmitting(true);
    setLastSubmittedIteration(null);
    setValidationError(null);
    
    try {
      // Step 1: Check if there are ingredients to validate
      if (productDetails.length === 0) {
        toast.warning("⚠️ No ingredients added. Add ingredients before submitting.", {
          duration: 5000,
          style: { background: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' }
        });
        setSubmitting(false);
        return;
      }

      // If stock deduction is enabled globally, perform validations
      if (globalStockDeductionEnabled) {
        // Step 2: Validate ingredients exist in Zoho
        toast.loading("🔍 Step 1/4: Validating ingredients in Zoho...", { id: "submit-progress" });
        const isValid = await validateIngredients();
        if (!isValid) {
          toast.error("❌ Ingredient validation failed. Check the error details above.", { 
            id: "submit-progress",
            duration: 5000,
            style: { background: '#FEE2E2', color: '#991B1B', border: '2px solid #DC2626' }
          });
          setSubmitting(false);
          return;
        }
        toast.success("✅ Step 1/4: Ingredients validated!", { id: "submit-progress", duration: 2000 });

        // Step 3: Validate vendor stock availability
        toast.loading("📦 Step 2/4: Checking vendor stock availability...", { id: "submit-progress" });
        const hasVendorStock = await validateVendorStock();
        if (!hasVendorStock) {
          toast.error("❌ Insufficient vendor stock. Check the error details above.", { 
            id: "submit-progress",
            duration: 5000,
            style: { background: '#FEE2E2', color: '#991B1B', border: '2px solid #DC2626' }
          });
          setSubmitting(false);
          return;
        }
        toast.success("✅ Step 2/4: Vendor stock verified!", { id: "submit-progress", duration: 2000 });
      } else {
        // Skip validation when stock deduction is disabled
        toast.info("ℹ️ Stock deduction disabled - skipping validation", { id: "submit-progress", duration: 2000 });
      }

      // Step 4: Save the current data
      toast.loading(globalStockDeductionEnabled ? "💾 Step 3/4: Saving formulation data..." : "💾 Saving formulation data...", { id: "submit-progress" });
      const savePayload = {
        productDetails: productDetails.map(pd => ({
          items: pd.items || "",
          inci: pd.inci || "",
          vendorDetails: pd.vendorDetails || "",
          composition: pd.composition || "",
          function: pd.function || ""
        })),
        testingDetails,
        testStatus,
        sku: iterationSku  // Include iteration-specific SKU
      };
      
      const saveResponse = await axios.put(
        `${API}/product-folders/${folderId}/iterations/${activeIteration}`,
        savePayload
      );
      
      if (!saveResponse.data.success) {
        toast.error("❌ Failed to save data. Please try again.", { 
          id: "submit-progress",
          duration: 5000,
          style: { background: '#FEE2E2', color: '#991B1B', border: '2px solid #DC2626' }
        });
        setSubmitting(false);
        return;
      }
      toast.success(globalStockDeductionEnabled ? "✅ Step 3/4: Data saved!" : "✅ Data saved!", { id: "submit-progress", duration: 2000 });
      
      // Step 5: Submit to Zoho (with or without stock deduction based on global toggle)
      toast.loading(globalStockDeductionEnabled ? "🚀 Step 4/4: Submitting to Zoho & deducting vendor stock..." : "🚀 Submitting to Zoho...", { id: "submit-progress" });
      const response = await axios.post(
        `${API}/product-folders/${folderId}/submit-to-zoho`,
        null,
        { params: { iteration_no: activeIteration, userId: user?.id, userName: user?.name, deductStock: globalStockDeductionEnabled } }
      );
      
      if (response.data.success) {
        // Show success with details
        toast.success(
          `🎉 Successfully submitted to Zoho!\n\nRecord ID: ${response.data.recordId}`, 
          { 
            id: "submit-progress",
            duration: 6000,
            style: { background: '#D1FAE5', color: '#065F46', border: '2px solid #10B981' }
          }
        );
        
        // Show stock deduction summary
        if (response.data.stockDeductions?.length > 0) {
          const deductionSummary = response.data.stockDeductions
            .map(d => `${d.ingredient} (${d.vendor}): -${d.deductedKg}kg`)
            .join('\n');
          
          toast.info(`📦 Stock Deducted:\n${deductionSummary}`, {
            duration: 8000,
            style: { background: '#DBEAFE', color: '#1E40AF', border: '1px solid #3B82F6' }
          });
        }
        
        // Track the submitted iteration for summary display
        setLastSubmittedIteration({
          iterationNo: activeIteration,
          recordId: response.data.recordId,
          timestamp: new Date().toLocaleString(),
          stockDeductions: response.data.stockDeductions || []
        });
        fetchFolder();
      } else {
        // Handle submission errors
        const errorMsg = response.data.message || "Submission failed";
        const validationErrors = response.data.validationErrors || [];
        
        if (validationErrors.length > 0) {
          setValidationError({
            error: "Zoho submission failed due to stock issues:",
            missingIngredients: [],
            noStockIngredients: [],
            vendorErrors: validationErrors
          });
        }
        
        toast.error(`❌ ${errorMsg}`, { 
          id: "submit-progress",
          duration: 5000,
          style: { background: '#FEE2E2', color: '#991B1B', border: '2px solid #DC2626' }
        });
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message || "Failed to submit to Zoho";
      toast.error(`❌ Error: ${errorDetail}`, { 
        id: "submit-progress",
        duration: 5000,
        style: { background: '#FEE2E2', color: '#991B1B', border: '2px solid #DC2626' }
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Update Test Status
  const handleTestStatusChange = async (newStatus) => {
    setTestStatus(newStatus);
    try {
      await axios.patch(`${API}/product-folders/${folderId}`, { testStatus: newStatus });
      toast.success(`✅ Status updated to "${newStatus}"`, {
        duration: 3000,
        style: { background: '#D1FAE5', color: '#065F46', border: '1px solid #10B981' }
      });
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("❌ Failed to update status", {
        style: { background: '#FEE2E2', color: '#991B1B', border: '1px solid #DC2626' }
      });
    }
  };

  // Create new iteration
  const handleCreateIteration = async () => {
    setCreatingIteration(true);
    try {
      const response = await axios.post(`${API}/product-folders/${folderId}/iterations`, {
        userId: user?.id,
        userName: user?.name
      });
      
      if (response.data.success) {
        toast.success("New iteration created! Previous data has been auto-filled.");
        setShowNewIterationModal(false);
        setLastSubmittedIteration(null); // Clear the submission summary
        fetchFolder();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create iteration");
    } finally {
      setCreatingIteration(false);
    }
  };

  // Delete iteration (Admin only - can delete ANY iteration including synced)
  const handleDeleteIteration = async () => {
    if (iterationToDelete === null) return;
    
    setDeletingIteration(true);
    try {
      const response = await axios.delete(
        `${API}/product-folders/${folderId}/iterations/${iterationToDelete}`,
        { params: { 
          userId: user?.id, 
          userName: user?.name, 
          userRole: user?.role,
          deleteIndex: deleteIterationIndex 
        }}
      );
      
      if (response.data.success) {
        const wasZohoSynced = response.data.wasZohoSynced;
        toast.success(`Iteration ${iterationToDelete} deleted successfully${wasZohoSynced ? ' (was synced to Zoho)' : ''}`);
        setShowDeleteIterationConfirm(false);
        setIterationToDelete(null);
        setDeleteIterationIndex(-1);
        fetchFolder();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete iteration";
      toast.error(errorMsg);
    } finally {
      setDeletingIteration(false);
    }
  };

  // Test Result Form handlers
  const handleTestResultFormChange = (field, value) => {
    setTestResultForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle multi-select observations toggle
  const handleObservationToggle = (observation) => {
    setTestResultForm(prev => {
      const currentObservations = prev.observations || [];
      const isSelected = currentObservations.includes(observation);
      
      if (isSelected) {
        return { ...prev, observations: currentObservations.filter(o => o !== observation) };
      } else {
        return { ...prev, observations: [...currentObservations, observation] };
      }
    });
  };

  // Initialize Test Result Form when opening
  const openTestResultForm = () => {
    const lastIterationNo = folder?.iterations?.length || 1;
    const lastIterationTestNo = `T${lastIterationNo}`;
    
    // Find the lab employee ID if assignedLabAssistant name is set
    const assignedTester = folder?.assignedLabAssistant || "";
    const testerEmployee = labEmployees.find(emp => emp.name === assignedTester);
    
    setTestResultForm({
      submissionDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      sampleNo: folder?.sampleNo || "",
      tester: assignedTester,
      testerLookupId: testerEmployee?.id || folder?.assignedLabAssistantId || "",
      finalTestNo: lastIterationTestNo, // Auto-fill with last iteration
      testStatusResult: "Pass", // Default to Pass for Test_Result form
      approvalStatus: "Approved",
      observations: [],
      comments: ""
    });
    setShowTestResultForm(true);
  };

  const handleTestResultFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTestResultFile(file);
    }
  };

  const resetTestResultForm = () => {
    // Auto-fill fields based on current folder data and last iteration
    const lastIterationNo = folder?.iterations?.length || 1;
    const lastIterationTestNo = `T${lastIterationNo}`;
    
    // Find the lab employee ID if assignedLabAssistant name is set
    const assignedTester = folder?.assignedLabAssistant || "";
    const testerEmployee = labEmployees.find(emp => emp.name === assignedTester);
    
    setTestResultForm({
      submissionDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      sampleNo: folder?.sampleNo || "",
      tester: assignedTester,
      testerLookupId: testerEmployee?.id || folder?.assignedLabAssistantId || "",
      finalTestNo: lastIterationTestNo,
      testStatusResult: "Pass", // Default to Pass
      approvalStatus: "Approved",
      observations: [],
      comments: ""
    });
    setTestResultFile(null);
  };

  const handleSubmitTestResult = async () => {
    if (!testResultForm.sampleNo) {
      toast.error("Please ensure Sample No is filled");
      return;
    }
    if (!testResultForm.tester) {
      toast.error("Please enter Tester Name");
      return;
    }

    setSubmittingTestResult(true);
    try {
      const formData = new FormData();
      formData.append("sampleNo", folder?.sampleNo || "");
      formData.append("sampleLookupId", folder?.sampleLookupId || "");
      formData.append("zohoRdRecordId", folder?.zohoRdRecordId || "");
      formData.append("productName", folder?.productName || "");
      formData.append("submissionDate", testResultForm.submissionDate);
      formData.append("formSampleNo", testResultForm.sampleNo);
      formData.append("tester", testResultForm.tester);
      formData.append("testerLookupId", testResultForm.testerLookupId || folder?.assignedLabAssistantId || ""); // Zoho lookup ID from dropdown or folder
      formData.append("finalTestNo", testResultForm.finalTestNo);
      formData.append("testStatus", testResultForm.testStatusResult);
      formData.append("approvalStatus", testResultForm.approvalStatus);
      // Multi-select observations - join as comma-separated string
      formData.append("observations", testResultForm.observations.join(", "));
      formData.append("comments", testResultForm.comments);
      
      if (testResultFile) {
        formData.append("file", testResultFile);
      }

      const response = await axios.post(`${API}/zoho/submit-test-result`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        // Check file upload result
        const fileResult = response.data.fileUpload;
        if (testResultFile && fileResult) {
          if (fileResult.success) {
            toast.success("🎉 Test Result submitted with file uploaded!", {
              description: `Record ID: ${response.data.recordId}`,
              duration: 5000
            });
          } else {
            toast.success("🎉 Test Result submitted!", {
              description: `File upload note: ${fileResult.error || "Could not upload file"}`,
              duration: 5000
            });
          }
        } else {
          toast.success("🎉 Test Result submitted successfully!", {
            description: `Record ID: ${response.data.recordId}`,
            duration: 5000
          });
        }
        toast.info("✅ You can now dispatch the sample", {
          description: "The Dispatch button is now available.",
          duration: 4000
        });
        
        // Set state BEFORE closing form - this ensures immediate UI update
        setTestResultSubmitted(true);
        setTestStatus("Complete");  // Auto-update test status to Complete
        setShowTestResultForm(false);
        resetTestResultForm();
        
        // Refresh folder data to get updated state from server
        fetchFolder();
      } else if (response.data.alreadySubmitted) {
        // Test result was already submitted - update state to reflect this
        toast.warning("Test Result has already been submitted for this sample.", {
          description: "Only one submission is allowed per record.",
          duration: 5000
        });
        setTestResultSubmitted(true);
        setShowTestResultForm(false);
        fetchFolder();
      } else {
        toast.error(response.data.message || "Submission failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit test result");
    } finally {
      setSubmittingTestResult(false);
    }
  };

  // ============ SAMPLE DISPATCH HANDLERS ============
  
  // Open dispatch form with auto-filled data
  const openDispatchForm = async () => {
    // Fetch complete sample details from backend (includes Client Email, Contact, Location, SKU, Product Type)
    const sampleNo = folder?.sampleNo || "";
    
    // Find if current user matches a lab employee for auto-fill
    const matchingEmployee = labEmployees.find(emp => 
      emp.name?.toLowerCase() === user?.name?.toLowerCase()
    );
    const dispatchedByDefault = matchingEmployee?.name || "";
    
    // Set initial values from folder - use folder.sku for SKU initially
    setDispatchForm({
      sampleNo: sampleNo,
      productName: folder?.productName || "",
      clientName: folder?.clientName || "",
      clientEmail: "",  // Will be fetched from API
      contactNumber: "",  // Will be fetched from API
      productType: folder?.productType || "",  // Use folder productType first
      packingType: folder?.packagingPreference || folder?.packingType || "",  // Try both field names
      location: "",  // Will be fetched from API
      samplePriority: folder?.testPriority || "",
      sku: folder?.sku || iterationSku || "",  // Use folder SKU or iteration SKU initially
      quantity: "",
      sampleImage: "",
      trackingNumber: "",
      dispatchDetails: [{ 
        modeOfDispatch: "", 
        dispatchType: "", 
        courierName: "", 
        dispatchDate: format(new Date(), "yyyy-MM-dd"), 
        expectedDeliveryDate: "" 
      }],
      handoverInfo: [{ 
        dispatchedBy: dispatchedByDefault,  // Auto-fill with matching lab employee name
        handoverTime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),  // Full datetime format
        fileUpload: "" 
      }],
      remarks: "",
      additionalRequirements: "",
      dispatchStatus: "Pending"
    });
    setShowDispatchForm(true);

    // Fetch complete details from API based on Sample No
    if (sampleNo) {
      try {
        const response = await axios.get(`${API}/sample-dispatch/details/${encodeURIComponent(sampleNo)}`);
        if (response.data.success && response.data.details) {
          const details = response.data.details;
          setDispatchForm(prev => ({
            ...prev,
            productName: details.productName || prev.productName,
            clientName: details.clientName || prev.clientName,
            clientEmail: details.clientEmail || "",
            contactNumber: details.contactNumber || "",
            productType: details.productType || prev.productType,
            packingType: details.packagingPreference || prev.packingType,
            location: details.location || "",
            samplePriority: details.samplePriority || prev.samplePriority,
            sku: details.sku || prev.sku  // Use API SKU or keep existing
          }));
          toast.success("Sample details loaded successfully");
        }
      } catch (error) {
        console.error("Failed to fetch sample details:", error);
        toast.error("Failed to load some sample details");
      }
    }
  };

  // Handle dispatch form field changes
  const handleDispatchFormChange = (field, value) => {
    setDispatchForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle dispatch details subform
  const addDispatchDetailRow = () => {
    setDispatchForm(prev => ({
      ...prev,
      dispatchDetails: [...prev.dispatchDetails, { 
        modeOfDispatch: "", 
        dispatchType: "", 
        courierName: "", 
        dispatchDate: "", 
        expectedDeliveryDate: "" 
      }]
    }));
  };

  const updateDispatchDetail = (index, field, value) => {
    setDispatchForm(prev => {
      const updated = [...prev.dispatchDetails];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, dispatchDetails: updated };
    });
  };

  const removeDispatchDetailRow = (index) => {
    if (dispatchForm.dispatchDetails.length > 1) {
      setDispatchForm(prev => ({
        ...prev,
        dispatchDetails: prev.dispatchDetails.filter((_, i) => i !== index)
      }));
    }
  };

  // Handle handover info subform
  const addHandoverInfoRow = () => {
    setDispatchForm(prev => ({
      ...prev,
      handoverInfo: [...prev.handoverInfo, { 
        dispatchedBy: "", 
        handoverTime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),  // Full datetime format
        fileUpload: "" 
      }]
    }));
  };

  const updateHandoverInfo = (index, field, value) => {
    setDispatchForm(prev => {
      const updated = [...prev.handoverInfo];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, handoverInfo: updated };
    });
  };

  const removeHandoverInfoRow = (index) => {
    if (dispatchForm.handoverInfo.length > 1) {
      setDispatchForm(prev => ({
        ...prev,
        handoverInfo: prev.handoverInfo.filter((_, i) => i !== index)
      }));
    }
  };

  // Handle sample image upload
  const handleDispatchImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setDispatchImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setDispatchForm(prev => ({ ...prev, sampleImage: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Reset dispatch form
  const resetDispatchForm = () => {
    setDispatchForm({
      sampleNo: "",
      productName: "",
      clientName: "",
      clientEmail: "",
      contactNumber: "",
      productType: "",
      packingType: "",
      location: "",
      samplePriority: "",
      sku: "",
      quantity: "",
      sampleImage: "",
      trackingNumber: "",
      dispatchDetails: [{ modeOfDispatch: "", dispatchType: "", courierName: "", dispatchDate: "", expectedDeliveryDate: "" }],
      handoverInfo: [{ dispatchedBy: "", handoverTime: format(new Date(), "yyyy-MM-dd'T'HH:mm"), fileUpload: "" }],
      remarks: "",
      additionalRequirements: "",
      dispatchStatus: "Pending"
    });
    setDispatchImageFile(null);
  };

  // Submit dispatch form
  const handleSubmitDispatch = async () => {
    // Validate required fields
    if (!dispatchForm.sampleNo) {
      toast.error("Sample No is required");
      return;
    }

    setSubmittingDispatch(true);
    try {
      const payload = {
        ...dispatchForm,
        createdBy: user?.name || "Admin"
      };

      const response = await axios.post(`${API}/sample-dispatch`, payload);

      if (response.data.success) {
        // Show success alert
        toast.success("🎉 Dispatch completed successfully!", {
          description: "Sample has been dispatched and is ready for delivery.",
          duration: 5000
        });
        
        // Show Zoho sync status
        if (response.data.zohoSync?.success) {
          toast.success("✅ Record synchronized with Zoho Creator", {
            description: `Record ID: ${response.data.zohoSync.recordId || "Created"}`,
            duration: 4000
          });
        } else if (response.data.zohoSync?.queuedForSync) {
          toast.info("📋 Queued for Zoho sync", {
            description: "The record will be synced when the API limit resets.",
            duration: 4000
          });
        }
        
        // Mark dispatch as submitted
        setDispatchSubmitted(true);
        setShowDispatchForm(false);
        resetDispatchForm();
      } else {
        toast.error(response.data.message || "Failed to create dispatch");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create dispatch");
    } finally {
      setSubmittingDispatch(false);
    }
  };

  // Check if test is complete to show Test Result button
  const isTestComplete = ["Complete", "Finish"].includes(testStatus);
  
  // Check if product is ready for dispatch (status is Completed)
  const isReadyForDispatch = testStatus === "Complete" || testStatus === "Finish";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-vcc-teal animate-spin" />
      </div>
    );
  }

  if (!folder) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h2 className="text-xl font-poppins font-semibold text-slate-800">Product not found</h2>
          <Button onClick={() => navigate("/products")} className="mt-4">Back to Products</Button>
        </div>
      </div>
    );
  }

  const currentIteration = folder.iterations?.find(it => it.iterationNo === activeIteration);
  const isIterationSynced = currentIteration?.zohoSyncStatus === "Synced" || currentIteration?.zohoRecordId || currentIteration?.zohoNpdRecordId;

  return (
    <div className="min-h-screen p-8" data-testid="product-detail-page">
      {/* Header */}
      <motion.div 
        className="flex items-center gap-4 mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Button 
          variant="ghost" 
          onClick={() => navigate("/products")} 
          className="hover:bg-vcc-pink-50"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-poppins font-bold text-slate-800">{folder.productName}</h1>
          <p className="text-slate-500 font-poppins">Sample No: {folder.sampleNo} • SKU: {folder.sku || "N/A"}</p>
        </div>
        
        {/* Test Status Dropdown */}
        <div className="flex items-center gap-3">
          <Label className="text-sm text-slate-600 font-poppins">Test Status:</Label>
          <Select value={testStatus} onValueChange={handleTestStatusChange}>
            <SelectTrigger className={`w-[150px] ${STATUS_COLORS[testStatus]} border-0 font-poppins font-medium`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEST_STATUS_OPTIONS.map(status => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Submit Test Result Button - Shows when test is Complete/Finish */}
          {/* Admin can always submit/resubmit test results */}
          {isTestComplete && (
            <Button
              onClick={() => {
                if (testResultSubmitted && !isAdmin) {
                  toast.info("Test Result has already been submitted for this product.", {
                    description: "You can view or edit the result in the Test Results report."
                  });
                } else {
                  openTestResultForm();
                }
              }}
              className={`font-poppins ${testResultSubmitted && !isAdmin
                ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' 
                : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
              disabled={testResultSubmitted && !isAdmin}
              data-testid="submit-test-result-btn"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              {testResultSubmitted && !isAdmin ? 'Test Result Submitted' : 'Submit Test Result'}
            </Button>
          )}
          
          {/* Dispatch Button - Admin can always access, others need isReadyForDispatch */}
          {(isAdmin || isReadyForDispatch) && (
            <Button
              onClick={() => {
                if (dispatchSubmitted && !isAdmin) {
                  toast.warning("Dispatch has already been completed for this product.", {
                    description: "Please check the Dispatch Report if you want to edit any information."
                  });
                } else {
                  openDispatchForm();
                }
              }}
              className={`font-poppins ${dispatchSubmitted && !isAdmin
                ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'} text-white`}
              data-testid="dispatch-sample-btn"
            >
              <Truck className="w-4 h-4 mr-2" />
              {dispatchSubmitted && !isAdmin ? 'Dispatched' : 'Dispatch'}
            </Button>
          )}
        </div>
      </motion.div>

      {/* Product Info Cards */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="bg-white/90 border-vcc-pink-100 shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Client</p>
            <p className="font-poppins font-medium text-slate-800">{folder.clientName || "N/A"}</p>
          </CardContent>
        </Card>
        <Card className="bg-white/90 border-vcc-pink-100 shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Lab Assistant</p>
            <p className="font-poppins font-medium text-slate-800">{folder.assignedLabAssistant || "Unassigned"}</p>
          </CardContent>
        </Card>
        <Card className="bg-white/90 border-vcc-pink-100 shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Priority</p>
            <p className="font-poppins font-medium text-slate-800">{folder.testPriority || "Normal"}</p>
          </CardContent>
        </Card>
        <Card className="bg-white/90 border-vcc-pink-100 shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Iterations</p>
            <p className="font-poppins font-bold text-vcc-teal text-lg">{folder.iterations?.length || 0}</p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Zoho Attachments/Files Section */}
      {folder.zohoFileUpload && (
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-poppins flex items-center gap-2">
                <Paperclip className="w-5 h-5 text-blue-600" />
                Zoho Attachments
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-4">
                {(() => {
                  // Parse the Zoho file path
                  const filePath = folder.zohoFileUpload;
                  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(filePath);
                  const fileName = filePath.split('/').pop()?.split('?')[0] || 'Attachment';
                  
                  // Extract filepath query param if present
                  let downloadUrl = `${API}/zoho/file/download/Research_and_Development_Report/${folder.zohoRdRecordId}/File_upload`;
                  if (filePath.includes('filepath=')) {
                    const filepathParam = filePath.split('filepath=')[1]?.split('&')[0];
                    if (filepathParam) {
                      downloadUrl += `?filepath=${encodeURIComponent(filepathParam)}`;
                    }
                  }
                  
                  return (
                    <div className="flex items-center gap-4 p-3 bg-white rounded-lg border border-blue-100 shadow-sm">
                      {isImage ? (
                        <div className="flex items-center gap-4">
                          <div className="relative group">
                            <img 
                              src={downloadUrl}
                              alt={fileName}
                              className="w-24 h-24 object-cover rounded-lg border border-blue-200 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => window.open(downloadUrl, '_blank')}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling?.classList.remove('hidden');
                              }}
                            />
                            <div className="hidden w-24 h-24 bg-slate-100 rounded-lg flex items-center justify-center">
                              <FileImage className="w-8 h-8 text-slate-400" />
                            </div>
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <ExternalLink className="w-6 h-6 text-white" />
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <p className="text-sm font-poppins font-medium text-slate-700 truncate max-w-[200px]">
                              {decodeURIComponent(fileName.replace(/_/g, ' '))}
                            </p>
                            <a 
                              href={downloadUrl}
                              download
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-poppins"
                            >
                              <Download className="w-3 h-3" />
                              Download
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FileUp className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex flex-col gap-1">
                            <p className="text-sm font-poppins font-medium text-slate-700 truncate max-w-[200px]">
                              {decodeURIComponent(fileName.replace(/_/g, ' '))}
                            </p>
                            <a 
                              href={downloadUrl}
                              download
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-poppins"
                            >
                              <Download className="w-3 h-3" />
                              Download File
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <p className="text-xs text-blue-500 font-poppins mt-3">
                Files synced from Zoho Creator R&D record
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Submission Success Summary Banner */}
      {lastSubmittedIteration && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 mb-6 shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 rounded-full p-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-poppins font-semibold text-emerald-800">
                  Iteration {lastSubmittedIteration.iterationNo} Submitted Successfully!
                </h3>
                <p className="text-sm text-emerald-600 font-poppins">
                  Zoho Record ID: {lastSubmittedIteration.recordId} • {lastSubmittedIteration.timestamp}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-slate-500 font-poppins">Total Iterations</p>
                <p className="font-poppins font-bold text-vcc-teal text-xl">{folder.iterations?.length || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500 font-poppins">Test Status</p>
                <Badge className={`${STATUS_COLORS[testStatus] || "bg-slate-100"} font-poppins`}>{testStatus}</Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLastSubmittedIteration(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center gap-4">
            <p className="text-sm text-emerald-700 font-poppins">
              Ready to create next iteration? The previous formula data will be auto-filled for easy adjustments.
            </p>
            <Button
              onClick={() => setShowNewIterationModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-poppins"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Next Iteration
            </Button>
          </div>
        </motion.div>
      )}

      {/* Iterations Tabs */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-poppins font-semibold text-slate-800">Formula Iterations</h2>
        <Button onClick={() => setShowNewIterationModal(true)} className="bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins">
          <Plus className="w-4 h-4 mr-2" />
          New Iteration
        </Button>
      </div>

      {folder.iterations?.length === 0 ? (
        <Card className="bg-white/90 border-vcc-pink-100 shadow-lg py-12 text-center">
          <Beaker className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500 font-poppins">No iterations yet. Click &quot;New Iteration&quot; to start.</p>
        </Card>
      ) : (
        <Tabs value={String(activeIteration)} onValueChange={(v) => handleIterationChange(Number(v))} className="space-y-6">
          <TabsList className="bg-white/80 border border-vcc-pink-100 p-1 shadow-lg flex-wrap">
            {folder.iterations.map((it, index) => {
              const SyncIcon = it.zohoSyncStatus === "Synced" ? CheckCircle2 : it.zohoSyncStatus === "Failed" ? XCircle : Clock;
              const isSynced = it.zohoRecordId || it.zohoSyncStatus === "Synced";
              // Admin can delete ANY iteration (including synced)
              const canDelete = isAdmin;
              
              return (
                <TabsTrigger
                  key={`${it.iterationNo}-${index}`}
                  value={String(it.iterationNo)}
                  className="data-[state=active]:bg-vcc-teal data-[state=active]:text-white font-poppins flex items-center gap-2 group relative pr-8"
                >
                  Iteration {it.iterationNo}
                  <Badge className={`text-xs ${STATUS_COLORS[it.result] || "bg-slate-100"}`}>{it.result || "Pending"}</Badge>
                  <SyncIcon className={`w-3 h-3 ${SYNC_COLORS[it.zohoSyncStatus] || "text-slate-400"}`} />
                  
                  {/* Delete button - Admin only, can delete ANY iteration */}
                  {canDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIterationToDelete(it.iterationNo);
                        setDeleteIterationIndex(index);
                        setShowDeleteIterationConfirm(true);
                      }}
                      className={`absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${isSynced ? 'hover:bg-orange-100' : 'hover:bg-red-100'}`}
                      title={isSynced ? "Delete iteration (synced to Zoho)" : "Delete iteration"}
                    >
                      <Trash2 className={`w-3 h-3 ${isSynced ? 'text-orange-500' : 'text-red-500'}`} />
                    </button>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {folder.iterations.map((iteration) => (
            <TabsContent key={iteration.iterationNo} value={String(iteration.iterationNo)} className="space-y-6">
              
              {/* Ingredient Validation Error Banner - High Visibility */}
              {validationError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-red-600 to-red-700 border-2 border-red-800 rounded-xl p-5 shadow-xl"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-white/20 rounded-full p-2">
                      <AlertTriangle className="w-7 h-7 text-white flex-shrink-0" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-poppins font-bold text-white text-lg">{validationError.error}</h3>
                      {validationError.missingIngredients?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm text-red-100 font-poppins font-semibold">⚠️ Missing in Zoho Ingredient Master:</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {validationError.missingIngredients.map((item, idx) => (
                              <Badge key={idx} className="bg-white text-red-700 font-poppins text-sm px-3 py-1 font-semibold">{item}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {validationError.noStockIngredients?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm text-yellow-200 font-poppins font-semibold">⚠️ Insufficient Vendor Stock:</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {validationError.noStockIngredients.map((item, idx) => (
                              <Badge key={idx} className="bg-yellow-400 text-yellow-900 font-poppins text-sm px-3 py-1 font-semibold">{item}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {validationError.vendorErrors?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm text-orange-200 font-poppins font-semibold">⚠️ Vendor Stock Issues:</p>
                          <ul className="mt-2 space-y-1">
                            {validationError.vendorErrors.map((err, idx) => (
                              <li key={idx} className="text-white text-sm font-poppins bg-red-800/50 rounded px-3 py-1">{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setValidationError(null)} 
                      className="text-white hover:bg-white/20 rounded-full p-2"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* AI Image Extraction Section */}
              <Card className="bg-gradient-to-r from-blue-50 to-vcc-pink-50 border-blue-200 shadow-lg" data-testid="image-extraction-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-blue-600" />
                    <CardTitle className="text-lg font-poppins text-slate-800">Import Formula from Image</CardTitle>
                    <Badge className="bg-blue-100 text-blue-700 font-poppins text-xs">AI Powered</Badge>
                  </div>
                  <p className="text-sm text-slate-500 font-poppins mt-1">Upload trial sheet images to auto-extract ingredients into the table below. Select which trial column to extract from.</p>
                </CardHeader>
                <CardContent>
                  {/* Trial Selection Dropdown */}
                  <div className="mb-4 flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-slate-600 font-poppins">Extract from Trial Column:</Label>
                      <Select 
                        value={selectedTrialForExtraction} 
                        onValueChange={setSelectedTrialForExtraction}
                      >
                        <SelectTrigger className="w-[100px] font-poppins border-blue-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TRIAL_EXTRACTION_OPTIONS.map(trial => {
                            // Check if this trial was already submitted (has a zohoSyncStatus of "Synced")
                            const submittedIterations = folder.iterations?.filter(it => it.zohoSyncStatus === "Synced").map(it => `T${it.iterationNo}`) || [];
                            const isSubmitted = submittedIterations.includes(trial);
                            return (
                              <SelectItem 
                                key={trial} 
                                value={trial}
                                disabled={isSubmitted}
                                className={isSubmitted ? "text-slate-400" : ""}
                              >
                                {trial} {isSubmitted && "(Submitted)"}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-slate-400 font-poppins">
                      Select which trial column (T1-T10) to extract data from your uploaded sheet
                    </p>
                  </div>

                  <div className="flex flex-col md:flex-row gap-4 items-start">
                    {/* File Upload Area */}
                    <div className="flex-1 w-full">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="file-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-24 border-dashed border-2 border-blue-300 hover:border-blue-400 hover:bg-blue-50/50 font-poppins"
                        data-testid="upload-trigger-btn"
                      >
                        <div className="text-center">
                          <Upload className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                          <span className="text-sm text-slate-600">Click to upload trial sheet images</span>
                          <p className="text-xs text-slate-400 mt-1">JPEG, PNG supported • Single or multiple trial sheets</p>
                        </div>
                      </Button>
                      
                      {/* Uploaded Files List */}
                      {uploadedFiles.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {uploadedFiles.map((file, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-200">
                              <div className="flex items-center gap-2">
                                <Image className="w-4 h-4 text-slate-400" />
                                <span className="text-sm text-slate-600 truncate max-w-[200px] font-poppins">{file.name}</span>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => removeUploadedFile(i)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Extract Button */}
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={handleExtractFromImages}
                        disabled={uploadedFiles.length < 1 || extracting || validating}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-poppins h-24 px-6"
                        data-testid="extract-btn"
                      >
                        {extracting ? (
                          <div className="flex flex-col items-center">
                            <Loader2 className="w-6 h-6 animate-spin mb-1" />
                            <span className="text-xs">Extracting {selectedTrialForExtraction}...</span>
                          </div>
                        ) : validating ? (
                          <div className="flex flex-col items-center">
                            <Loader2 className="w-6 h-6 animate-spin mb-1" />
                            <span className="text-xs">Validating...</span>
                          </div>
                        ) : extractionComplete ? (
                          <div className="flex flex-col items-center">
                            <CheckCircle2 className="w-6 h-6 mb-1" />
                            <span className="text-xs">Extracted!</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <CloudUpload className="w-6 h-6 mb-1" />
                            <span className="text-xs">Extract {selectedTrialForExtraction}</span>
                          </div>
                        )}
                      </Button>
                      {uploadedFiles.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearUploadedFiles}
                          className="text-xs text-slate-500 font-poppins"
                        >
                          Clear files
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {extractionComplete && (
                    <p className="text-sm text-green-600 mt-3 flex items-center gap-1 font-poppins">
                      <CheckCircle2 className="w-4 h-4" /> Ingredients extracted from {selectedTrialForExtraction} and added to table below
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Product Composition Details Section */}
              <Card className="bg-white/90 border-vcc-pink-100 shadow-lg">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-vcc-teal" />
                    <CardTitle className="text-lg font-poppins text-slate-800">Product Composition Details</CardTitle>
                  </div>
                  <Button size="sm" variant="outline" onClick={handleAddProductRow} className="border-vcc-teal text-vcc-teal hover:bg-vcc-teal/10 font-poppins">
                    <Plus className="w-4 h-4 mr-1" /> Add Row
                  </Button>
                </CardHeader>
                <CardContent>
                  {/* Iteration SKU Field */}
                  <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1">
                        <Label className="text-sm font-poppins font-semibold text-amber-800 flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          SKU Size (grams) for Stock Deduction
                        </Label>
                        <p className="text-xs text-amber-600 mt-1 font-poppins">
                          This SKU value will be used to calculate stock deductions for this iteration. Auto-populated from product, but can be changed if needed.
                        </p>
                      </div>
                      <div className="w-full md:w-48">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={iterationSku}
                          onChange={(e) => setIterationSku(e.target.value)}
                          placeholder="e.g., 100"
                          className="h-10 font-poppins border-amber-300 bg-white text-lg font-semibold text-center"
                          data-testid="iteration-sku-input"
                        />
                        <p className="text-xs text-amber-500 mt-1 text-center font-poppins">
                          Default: {folder?.sku || "100"}g
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-vcc-cream-50">
                          <TableHead className="font-poppins text-slate-700">Ingredient Name</TableHead>
                          <TableHead className="font-poppins text-slate-700">INCI</TableHead>
                          <TableHead className="font-poppins text-slate-700">Vendor</TableHead>
                          <TableHead className="font-poppins text-slate-700">Composition %</TableHead>
                          <TableHead className="font-poppins text-slate-700">Function</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productDetails.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-slate-400 font-poppins">
                              No ingredients added. Click &quot;Add Row&quot; or use AI extraction above.
                            </TableCell>
                          </TableRow>
                        ) : (
                          productDetails.map((row) => (
                            <TableRow key={row.id} className="hover:bg-vcc-pink-50/30">
                              <TableCell>
                                <Input
                                  value={row.items || ""}
                                  onChange={(e) => handleProductChange(row.id, "items", e.target.value)}
                                  placeholder="e.g., Aqua"
                                  className="h-8 font-poppins border-vcc-pink-100"
                                  data-testid={`ingredient-name-${row.id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={row.inci || ""}
                                  onChange={(e) => handleProductChange(row.id, "inci", e.target.value)}
                                  placeholder="e.g., Water"
                                  className="h-8 font-poppins border-vcc-pink-100"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={row.vendorDetails || ""}
                                  onChange={(e) => handleProductChange(row.id, "vendorDetails", e.target.value)}
                                  placeholder="Vendor name"
                                  className="h-8 font-poppins border-vcc-pink-100"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={row.composition || ""}
                                  onChange={(e) => handleProductChange(row.id, "composition", e.target.value)}
                                  placeholder="%"
                                  className="h-8 w-20 font-poppins border-vcc-pink-100"
                                />
                              </TableCell>
                              <TableCell>
                                <div className="relative">
                                  <Input
                                    value={row.function || ""}
                                    onChange={(e) => handleProductChange(row.id, "function", e.target.value)}
                                    placeholder={row.fetchingFunction ? "Fetching..." : "e.g., Solvent"}
                                    className={`h-8 font-poppins border-vcc-pink-100 ${row.fetchingFunction ? 'bg-slate-50' : ''}`}
                                    disabled={row.fetchingFunction}
                                  />
                                  {row.fetchingFunction && (
                                    <Loader2 className="w-4 h-4 absolute right-2 top-2 animate-spin text-vcc-teal" />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="sm" onClick={() => handleRemoveProductRow(row.id)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Testing Details */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-white/90 border-vcc-pink-100 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-poppins text-slate-800">Preparation & Observations</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">Method of Preparation</Label>
                      <Textarea
                        value={testingDetails.methodOfPreparation || ""}
                        onChange={(e) => handleTestingChange("methodOfPreparation", e.target.value)}
                        className="mt-1 font-poppins border-vcc-pink-100"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">Observation</Label>
                      <Textarea
                        value={testingDetails.observation || ""}
                        onChange={(e) => handleTestingChange("observation", e.target.value)}
                        className="mt-1 font-poppins border-vcc-pink-100"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">Description</Label>
                      <Textarea
                        value={testingDetails.description || ""}
                        onChange={(e) => handleTestingChange("description", e.target.value)}
                        className="mt-1 font-poppins border-vcc-pink-100"
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/90 border-vcc-pink-100 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-poppins text-slate-800">Testing Parameters</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">pH Range</Label>
                      <Input value={testingDetails.phRange || ""} onChange={(e) => handleTestingChange("phRange", e.target.value)} className="mt-1 font-poppins border-vcc-pink-100" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">pH Initial</Label>
                      <Input value={testingDetails.phInitial || ""} onChange={(e) => handleTestingChange("phInitial", e.target.value)} className="mt-1 font-poppins border-vcc-pink-100" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">pH Final</Label>
                      <Input value={testingDetails.phFinal || ""} onChange={(e) => handleTestingChange("phFinal", e.target.value)} className="mt-1 font-poppins border-vcc-pink-100" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">Viscosity</Label>
                      <Input value={testingDetails.viscosity || ""} onChange={(e) => handleTestingChange("viscosity", e.target.value)} className="mt-1 font-poppins border-vcc-pink-100" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">Odour</Label>
                      <Select value={testingDetails.odour || ""} onValueChange={(v) => handleTestingChange("odour", v)}>
                        <SelectTrigger className="mt-1 font-poppins border-vcc-pink-100"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{ODOUR_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">Texture</Label>
                      <Select value={testingDetails.texture || ""} onValueChange={(v) => handleTestingChange("texture", v)}>
                        <SelectTrigger className="mt-1 font-poppins border-vcc-pink-100"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{TEXTURE_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">Colour</Label>
                      <Select value={testingDetails.color || ""} onValueChange={(v) => handleTestingChange("color", v)}>
                        <SelectTrigger className="mt-1 font-poppins border-vcc-pink-100"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{COLOUR_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 font-poppins">Test Result</Label>
                      <Select value={testingDetails.testResult || ""} onValueChange={(v) => handleTestingChange("testResult", v)}>
                        <SelectTrigger className="mt-1 font-poppins border-vcc-pink-100"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{TEST_RESULT_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm text-slate-600 font-poppins">Direction of Use</Label>
                      <Textarea
                        value={testingDetails.directionOfUse || ""}
                        onChange={(e) => handleTestingChange("directionOfUse", e.target.value)}
                        placeholder="e.g., Apply a small amount to skin and massage gently..."
                        className="mt-1 font-poppins border-vcc-pink-100"
                        rows={2}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm text-slate-600 font-poppins">Remarks</Label>
                      <Textarea
                        value={testingDetails.remarks || ""}
                        onChange={(e) => handleTestingChange("remarks", e.target.value)}
                        placeholder="Additional notes..."
                        className="mt-1 font-poppins border-vcc-pink-100"
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={handleSave}
                  disabled={saving}
                  className="border-vcc-teal text-vcc-teal hover:bg-vcc-teal/10 font-poppins"
                  data-testid="save-draft-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Draft
                </Button>
                
                {/* Stock Deduction Toggle - Global setting controlled by Admin */}
                <div className={`flex items-center gap-3 px-4 py-2 rounded-lg border ${isAdmin ? 'bg-slate-50 border-slate-200' : 'bg-slate-100 border-slate-300'}`}>
                  <Switch
                    id="stock-deduction-toggle"
                    checked={globalStockDeductionEnabled}
                    onCheckedChange={handleToggleGlobalStockDeduction}
                    disabled={!isAdmin || loadingGlobalSetting}
                    data-testid="stock-deduction-toggle"
                    className={`data-[state=checked]:bg-vcc-teal ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  <Label 
                    htmlFor="stock-deduction-toggle" 
                    className={`text-sm font-medium cursor-pointer ${!isAdmin ? 'text-slate-500' : 'text-slate-700'}`}
                    title={!isAdmin ? "Only Admin can change this setting" : "Toggle stock deduction globally"}
                  >
                    {loadingGlobalSetting ? (
                      <span className="flex items-center gap-1.5 text-slate-500">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Loading...
                      </span>
                    ) : globalStockDeductionEnabled ? (
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        Stock Deduction ON
                        {!isAdmin && <span className="text-xs text-slate-400 ml-1">(Admin controlled)</span>}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-amber-600">
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                        Stock Deduction OFF
                        {!isAdmin && <span className="text-xs text-slate-400 ml-1">(Admin controlled)</span>}
                      </span>
                    )}
                  </Label>
                </div>
                
                <Button
                  onClick={handleSubmitToZoho}
                  disabled={submitting || validating || isIterationSynced}
                  className={`font-poppins ${isIterationSynced ? 'bg-slate-400 cursor-not-allowed' : 'bg-vcc-teal hover:bg-vcc-teal/90'} text-white`}
                  data-testid="submit-to-zoho-btn"
                  title={isIterationSynced ? "This iteration has already been submitted and synced to Zoho" : "Submit to Zoho"}
                >
                  {validating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Validating...
                    </>
                  ) : submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : isIterationSynced ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Already Submitted
                    </>
                  ) : (
                    <>
                      <CloudUpload className="w-4 h-4 mr-2" />
                      Submit to Zoho
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* New Iteration Modal */}
      <Dialog open={showNewIterationModal} onOpenChange={setShowNewIterationModal}>
        <DialogContent className="bg-white border-vcc-pink-100">
          <DialogHeader>
            <DialogTitle className="font-poppins text-slate-800">Create New Iteration</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 font-poppins">
            Create <strong>Iteration {(folder.iterations?.length || 0) + 1}</strong> for {folder.productName}?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewIterationModal(false)} className="font-poppins">Cancel</Button>
            <Button onClick={handleCreateIteration} disabled={creatingIteration} className="bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins">
              {creatingIteration ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Result Form Modal */}
      <Dialog open={showTestResultForm} onOpenChange={setShowTestResultForm}>
        <DialogContent className="bg-white border-vcc-pink-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-poppins text-slate-800 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-600" />
              Submit Test Result
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Research Lookup Info */}
            <div className="bg-vcc-cream-50 p-4 rounded-lg">
              <p className="text-sm text-slate-500 font-poppins">Research Lookup (Sample)</p>
              <p className="font-poppins font-medium text-slate-800">{folder.sampleNo} - {folder.productName}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Sample No - Auto-filled */}
              <div>
                <Label className="text-sm text-slate-600 font-poppins">Sample No *</Label>
                <Input
                  value={testResultForm.sampleNo}
                  onChange={(e) => handleTestResultFormChange("sampleNo", e.target.value)}
                  placeholder="Auto-filled from Research"
                  className="mt-1 font-poppins border-vcc-pink-100 bg-slate-50"
                  readOnly
                />
                <p className="text-xs text-slate-400 mt-1 font-poppins">Auto-filled from Research form</p>
              </div>

              {/* Tester - Dropdown from Lab Employees */}
              <div>
                <Label className="text-sm text-slate-600 font-poppins">Tester Name *</Label>
                <Select 
                  value={testResultForm.testerLookupId || ""} 
                  onValueChange={(id) => {
                    const employee = labEmployees.find(emp => emp.id === id);
                    setTestResultForm(prev => ({
                      ...prev,
                      tester: employee?.name || "",
                      testerLookupId: id
                    }));
                  }}
                >
                  <SelectTrigger className="mt-1 font-poppins border-vcc-pink-100">
                    <SelectValue placeholder={loadingEmployees ? "Loading..." : "Select Tester"}>
                      {testResultForm.tester || "Select Tester"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {labEmployees.length === 0 ? (
                      <SelectItem value="_none" disabled>No employees found</SelectItem>
                    ) : (
                      labEmployees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1 font-poppins">Select from Lab Employees</p>
              </div>

              {/* Submission Date */}
              <div>
                <Label className="text-sm text-slate-600 font-poppins">Submission Date *</Label>
                <Input
                  type="datetime-local"
                  value={testResultForm.submissionDate}
                  onChange={(e) => handleTestResultFormChange("submissionDate", e.target.value)}
                  className="mt-1 font-poppins border-vcc-pink-100"
                />
              </div>

              {/* Final Test No - Auto-filled with last iteration */}
              <div>
                <Label className="text-sm text-slate-600 font-poppins">Final Test No *</Label>
                <Select value={testResultForm.finalTestNo} onValueChange={(v) => handleTestResultFormChange("finalTestNo", v)}>
                  <SelectTrigger className="mt-1 font-poppins border-vcc-pink-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FINAL_TEST_NO_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1 font-poppins">Auto-filled with last iteration (T{folder?.iterations?.length || 1})</p>
              </div>
            </div>

            {/* Test Status - Radio */}
            <div>
              <Label className="text-sm text-slate-600 font-poppins mb-3 block">Test Status</Label>
              <RadioGroup
                value={testResultForm.testStatusResult}
                onValueChange={(v) => handleTestResultFormChange("testStatusResult", v)}
                className="flex gap-6"
              >
                {TEST_STATUS_FORM_OPTIONS.map(status => (
                  <div key={status} className="flex items-center space-x-2">
                    <RadioGroupItem value={status} id={`test-status-${status}`} />
                    <Label htmlFor={`test-status-${status}`} className="font-poppins text-slate-700 cursor-pointer">
                      {status}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Approval Status - Radio */}
            <div>
              <Label className="text-sm text-slate-600 font-poppins mb-3 block">Approval Status</Label>
              <RadioGroup
                value={testResultForm.approvalStatus}
                onValueChange={(v) => handleTestResultFormChange("approvalStatus", v)}
                className="flex gap-6"
              >
                {APPROVAL_STATUS_OPTIONS.map(status => (
                  <div key={status} className="flex items-center space-x-2">
                    <RadioGroupItem value={status} id={`approval-${status}`} />
                    <Label htmlFor={`approval-${status}`} className="font-poppins text-slate-700 cursor-pointer">
                      {status}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Observations - Multi-select */}
            <div>
              <Label className="text-sm text-slate-600 font-poppins mb-3 block">Observations (Multi-select)</Label>
              <div className="flex flex-wrap gap-2">
                {OBSERVATIONS_OPTIONS.map(observation => {
                  const isSelected = testResultForm.observations?.includes(observation);
                  return (
                    <button
                      key={observation}
                      type="button"
                      onClick={() => handleObservationToggle(observation)}
                      className={`px-3 py-2 rounded-lg border text-sm font-poppins transition-all ${
                        isSelected 
                          ? 'bg-vcc-teal text-white border-vcc-teal shadow-md' 
                          : 'bg-white text-slate-600 border-vcc-pink-200 hover:border-vcc-teal hover:bg-vcc-teal/5'
                      }`}
                    >
                      {isSelected && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                      {observation}
                    </button>
                  );
                })}
              </div>
              {testResultForm.observations?.length > 0 && (
                <p className="text-xs text-slate-500 mt-2 font-poppins">
                  Selected: {testResultForm.observations.join(", ")}
                </p>
              )}
            </div>

            {/* File Upload */}
            <div>
              <Label className="text-sm text-slate-600 font-poppins mb-2 block">File Upload</Label>
              <input
                ref={testResultFileRef}
                type="file"
                onChange={handleTestResultFileSelect}
                className="hidden"
              />
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => testResultFileRef.current?.click()}
                  className="border-dashed border-2 border-vcc-pink-200 hover:border-vcc-pink-400 font-poppins"
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  Choose File
                </Button>
                {testResultFile && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="font-poppins truncate max-w-[200px]">{testResultFile.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => setTestResultFile(null)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Comments */}
            <div>
              <Label className="text-sm text-slate-600 font-poppins">Comments</Label>
              <Textarea
                value={testResultForm.comments}
                onChange={(e) => handleTestResultFormChange("comments", e.target.value)}
                placeholder="Enter any additional comments..."
                className="mt-1 font-poppins border-vcc-pink-100"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={resetTestResultForm}
              className="font-poppins"
            >
              Reset
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowTestResultForm(false)} 
              className="font-poppins"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitTestResult}
              disabled={submittingTestResult}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-poppins"
            >
              {submittingTestResult ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CloudUpload className="w-4 h-4 mr-2" />
              )}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit to Zoho Confirmation Dialog */}
      <AlertDialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-poppins">
              <CloudUpload className="w-5 h-5 text-vcc-teal" />
              Confirm Zoho Submission
            </AlertDialogTitle>
            <AlertDialogDescription className="font-poppins">
              This will submit the iteration to Zoho Creator and automatically deduct ingredient stock based on SKU size and composition percentages.
              <br /><br />
              <strong>Actions that will be performed:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li>Submit formulation data to Zoho NPD form</li>
                <li>Update Test Status to &quot;Started&quot;</li>
                <li>Deduct ingredient stock from Ingredient Master</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-poppins">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmSubmitToZoho}
              className="bg-vcc-teal hover:bg-vcc-teal/90 font-poppins"
            >
              Yes, Submit to Zoho
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Row Confirmation Dialog */}
      <AlertDialog open={showDeleteRowConfirm} onOpenChange={setShowDeleteRowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-poppins text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Ingredient Row
            </AlertDialogTitle>
            <AlertDialogDescription className="font-poppins">
              Are you sure you want to remove this ingredient from the formulation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-poppins">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteRow}
              className="bg-red-600 hover:bg-red-700 font-poppins"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Iteration Confirmation Dialog */}
      <AlertDialog open={showDeleteIterationConfirm} onOpenChange={(open) => {
        setShowDeleteIterationConfirm(open);
        if (!open) {
          setIterationToDelete(null);
          setDeleteIterationIndex(-1);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-poppins text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Iteration {iterationToDelete}?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-poppins">
              Are you sure you want to delete <strong>Iteration {iterationToDelete}</strong>? 
              This action cannot be undone.
              <br /><br />
              {deleteIterationIndex >= 0 && folder?.iterations?.[deleteIterationIndex]?.zohoSyncStatus === "Synced" ? (
                <span className="text-orange-600 text-sm font-medium">
                  ⚠️ Warning: This iteration has been submitted to Zoho. Deleting it here will only remove it from the portal, not from Zoho Creator.
                </span>
              ) : (
                <span className="text-slate-500 text-sm">
                  This iteration has not been submitted to Zoho yet.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-poppins" disabled={deletingIteration}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteIteration}
              className="bg-red-600 hover:bg-red-700 font-poppins"
              disabled={deletingIteration}
            >
              {deletingIteration ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete Iteration"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ============ SAMPLE DISPATCH FORM DIALOG ============ */}
      <Dialog open={showDispatchForm} onOpenChange={setShowDispatchForm}>
        <DialogContent className="bg-white border-vcc-pink-100 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-poppins text-slate-800 flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Sample Dispatch Form
            </DialogTitle>
            <DialogDescription className="font-poppins text-slate-500">
              Dispatch sample to client with tracking details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Product Information - Auto-filled */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
              <h3 className="text-sm font-semibold text-blue-800 font-poppins mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Product Information (Auto-filled)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">Sample No</Label>
                  <Input
                    value={dispatchForm.sampleNo}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">Product Name</Label>
                  <Input
                    value={dispatchForm.productName}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">Client Name</Label>
                  <Input
                    value={dispatchForm.clientName}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">Client Email</Label>
                  <Input
                    value={dispatchForm.clientEmail}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">Contact Number</Label>
                  <Input
                    value={dispatchForm.contactNumber}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">Product Type</Label>
                  <Input
                    value={dispatchForm.productType}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Packing Type</Label>
                  <Select
                    value={dispatchForm.packingType}
                    onValueChange={(v) => handleDispatchFormChange("packingType", v)}
                  >
                    <SelectTrigger className="mt-1 font-poppins text-sm">
                      <SelectValue placeholder="Select Packing Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bottle">Bottle</SelectItem>
                      <SelectItem value="Jar">Jar</SelectItem>
                      <SelectItem value="Tube">Tube</SelectItem>
                      <SelectItem value="Pouch">Pouch</SelectItem>
                      <SelectItem value="Box">Box</SelectItem>
                      <SelectItem value="Sachet">Sachet</SelectItem>
                      <SelectItem value="Container">Container</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">Location</Label>
                  <Input
                    value={dispatchForm.location}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">Priority</Label>
                  <Input
                    value={dispatchForm.samplePriority}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-poppins">SKU</Label>
                  <Input
                    value={dispatchForm.sku}
                    readOnly
                    className="mt-1 font-poppins bg-white/80 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Vendor & Sample Details - Editable */}
            <div className="bg-white p-4 rounded-xl border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800 font-poppins mb-3">
                Vendor & Sample Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Quantity *</Label>
                  <Input
                    value={dispatchForm.quantity}
                    onChange={(e) => handleDispatchFormChange("quantity", e.target.value)}
                    placeholder="e.g., 10 units"
                    className="mt-1 font-poppins"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Tracking Number</Label>
                  <Input
                    value={dispatchForm.trackingNumber}
                    onChange={(e) => handleDispatchFormChange("trackingNumber", e.target.value)}
                    placeholder="Enter tracking number"
                    className="mt-1 font-poppins"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Sample Image</Label>
                  <input
                    type="file"
                    ref={dispatchImageRef}
                    onChange={handleDispatchImageSelect}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => dispatchImageRef.current?.click()}
                      className="font-poppins"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                    {dispatchImageFile && (
                      <span className="text-xs text-slate-500 truncate max-w-[100px]">
                        {dispatchImageFile.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Dispatch Details Subform */}
            <div className="bg-white p-4 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800 font-poppins">
                  Dispatch Details
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addDispatchDetailRow}
                  className="font-poppins text-blue-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Row
                </Button>
              </div>
              <div className="space-y-3">
                {dispatchForm.dispatchDetails.map((detail, index) => (
                  <div key={index} className="grid grid-cols-6 gap-2 items-end bg-slate-50 p-3 rounded-lg">
                    <div>
                      <Label className="text-xs text-slate-600 font-poppins">Mode of Dispatch</Label>
                      <Select
                        value={detail.modeOfDispatch}
                        onValueChange={(v) => updateDispatchDetail(index, "modeOfDispatch", v)}
                      >
                        <SelectTrigger className="mt-1 font-poppins text-sm h-9">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Courier">Courier</SelectItem>
                          <SelectItem value="Hand Delivery">Hand Delivery</SelectItem>
                          <SelectItem value="Logistics Partner">Logistics Partner</SelectItem>
                          <SelectItem value="Transport Agency">Transport Agency</SelectItem>
                          <SelectItem value="Local Delivery">Local Delivery</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 font-poppins">Dispatch Type</Label>
                      <Select
                        value={detail.dispatchType}
                        onValueChange={(v) => updateDispatchDetail(index, "dispatchType", v)}
                      >
                        <SelectTrigger className="mt-1 font-poppins text-sm h-9">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="By Road">By Road</SelectItem>
                          <SelectItem value="By Air">By Air</SelectItem>
                          <SelectItem value="By Sea">By Sea</SelectItem>
                          <SelectItem value="By Rail">By Rail</SelectItem>
                          <SelectItem value="Hand Delivery">Hand Delivery</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 font-poppins">Courier Name</Label>
                      <Select
                        value={detail.courierName}
                        onValueChange={(v) => updateDispatchDetail(index, "courierName", v)}
                      >
                        <SelectTrigger className="mt-1 font-poppins text-sm h-9">
                          <SelectValue placeholder="Select Courier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Blue Dart">Blue Dart</SelectItem>
                          <SelectItem value="Porter">Porter</SelectItem>
                          <SelectItem value="DTDC">DTDC</SelectItem>
                          <SelectItem value="Delhivery">Delhivery</SelectItem>
                          <SelectItem value="Xpressbees">Xpressbees</SelectItem>
                          <SelectItem value="Ecom Express">Ecom Express</SelectItem>
                          <SelectItem value="India Post">India Post</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 font-poppins">Dispatch Date</Label>
                      <Input
                        type="date"
                        value={detail.dispatchDate}
                        onChange={(e) => updateDispatchDetail(index, "dispatchDate", e.target.value)}
                        className="mt-1 font-poppins text-sm h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 font-poppins">Expected Delivery</Label>
                      <Input
                        type="date"
                        value={detail.expectedDeliveryDate}
                        onChange={(e) => updateDispatchDetail(index, "expectedDeliveryDate", e.target.value)}
                        className="mt-1 font-poppins text-sm h-9"
                      />
                    </div>
                    <div className="flex justify-center">
                      {dispatchForm.dispatchDetails.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDispatchDetailRow(index)}
                          className="text-red-500 hover:text-red-700 h-9"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Handover Information Subform */}
            <div className="bg-white p-4 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800 font-poppins">
                  Handover Information
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addHandoverInfoRow}
                  className="font-poppins text-blue-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Row
                </Button>
              </div>
              <div className="space-y-3">
                {dispatchForm.handoverInfo.map((info, index) => (
                  <div key={index} className="grid grid-cols-4 gap-3 items-end bg-slate-50 p-3 rounded-lg">
                    <div>
                      <Label className="text-xs text-slate-600 font-poppins">Dispatched By *</Label>
                      <Select
                        value={info.dispatchedBy}
                        onValueChange={(v) => updateHandoverInfo(index, "dispatchedBy", v)}
                      >
                        <SelectTrigger className="mt-1 font-poppins text-sm">
                          <SelectValue placeholder="Select Person" />
                        </SelectTrigger>
                        <SelectContent>
                          {labEmployees.map(emp => (
                            <SelectItem key={emp.id || emp.name} value={emp.name}>
                              {emp.name}
                            </SelectItem>
                          ))}
                          {/* Fallback options if labEmployees is empty */}
                          {labEmployees.length === 0 && (
                            <>
                              <SelectItem value="Srujana Vaddepelli">Srujana Vaddepelli</SelectItem>
                              <SelectItem value="Manisha Muppidi">Manisha Muppidi</SelectItem>
                              <SelectItem value="Trinadh Ananthu">Trinadh Ananthu</SelectItem>
                              <SelectItem value="Swapnil Tirmanwar">Swapnil Tirmanwar</SelectItem>
                              <SelectItem value="Nirosha Bukya">Nirosha Bukya</SelectItem>
                              <SelectItem value="Rahul Jarapla">Rahul Jarapla</SelectItem>
                              <SelectItem value="Shameem S">Shameem S</SelectItem>
                              <SelectItem value="Swikruthi Timmayapally">Swikruthi Timmayapally</SelectItem>
                              <SelectItem value="Amulya Narra">Amulya Narra</SelectItem>
                              <SelectItem value="Venkatesh Naidu">Venkatesh Naidu</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 font-poppins">Handover Time</Label>
                      <Input
                        type="datetime-local"
                        value={info.handoverTime}
                        onChange={(e) => updateHandoverInfo(index, "handoverTime", e.target.value)}
                        className="mt-1 font-poppins text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 font-poppins">File Upload</Label>
                      <Input
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              updateHandoverInfo(index, "fileUpload", ev.target.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="mt-1 font-poppins text-sm"
                      />
                    </div>
                    <div className="flex justify-center">
                      {dispatchForm.handoverInfo.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeHandoverInfoRow(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dispatch Status & Remarks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-600 font-poppins">Dispatch Status</Label>
                <Select
                  value={dispatchForm.dispatchStatus}
                  onValueChange={(v) => handleDispatchFormChange("dispatchStatus", v)}
                >
                  <SelectTrigger className="mt-1 font-poppins">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Dispatched">Dispatched</SelectItem>
                    <SelectItem value="In Transit">In Transit</SelectItem>
                    <SelectItem value="Delivered">Delivered</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-poppins">Remarks / Notes</Label>
                <Textarea
                  value={dispatchForm.remarks}
                  onChange={(e) => handleDispatchFormChange("remarks", e.target.value)}
                  placeholder="Enter any additional notes..."
                  className="mt-1 font-poppins"
                  rows={2}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-poppins">Additional Requirements</Label>
                <Textarea
                  value={dispatchForm.additionalRequirements}
                  onChange={(e) => handleDispatchFormChange("additionalRequirements", e.target.value)}
                  placeholder="Enter any additional requirements..."
                  className="mt-1 font-poppins"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={resetDispatchForm}
              className="font-poppins"
            >
              Reset
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowDispatchForm(false)} 
              className="font-poppins"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitDispatch}
              disabled={submittingDispatch}
              className="bg-blue-600 hover:bg-blue-700 text-white font-poppins"
            >
              {submittingDispatch ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Truck className="w-4 h-4 mr-2" />
              )}
              Create Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
